import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { BadgeConfig, BadgeDropdownCell } from "@tellescope/ui";
import { i18n } from "@tellescope/i18n";

interface PriorityCellProps {
  priority: TellescopeModelFromName<"tickets">["priority"];
  ticketId: string;
  disableChangeStage?: boolean;
}

const AVAILABLE_PRIORITIES = [0, 10, 5, 1] as const;

const getPriorityConfig = (priority: TellescopeModelFromName<"tickets">["priority"]): BadgeConfig => {
  switch (priority) {
    case 1:
      return { label: i18n.t("Low", { ns: "tickets" }), bgColor: "bg-green-200", textColor: "text-green-900" };
    case 5:
      return { label: i18n.t("Medium", { ns: "tickets" }), bgColor: "bg-yellow-200", textColor: "text-yellow-900" };
    case 10:
      return { label: i18n.t("High", { ns: "tickets" }), bgColor: "bg-red-500", textColor: "text-red-50" };
    default:
      return { label: i18n.t("Not Set", { ns: "tickets" }), bgColor: "bg-muted", textColor: "text-muted-foreground" };
  }
};

export const PriorityCell = ({ priority, ticketId, disableChangeStage }: PriorityCellProps) => {
  const { t } = useTranslation("tickets");

  const updateMutation = useTellescopeMutation({
    model: "tickets",
    method: "updateOne",
  });

  const handleChange = useCallback(
    (newPriority: TellescopeModelFromName<"tickets">["priority"]) => {
      updateMutation.mutateAsync({ id: ticketId, priority: newPriority });
    },
    [ticketId, updateMutation]
  );

  return (
    <BadgeDropdownCell<number | undefined>
      value={priority}
      options={AVAILABLE_PRIORITIES}
      getConfig={getPriorityConfig}
      onChange={handleChange}
      disabled={disableChangeStage}
      unknownLabel={t("Unknown Priority")}
    />
  );
};
